import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './GameView.css';

const API_URL = process.env.REACT_APP_API_URL;

function GameView() {
  const { gameId } = useParams();
  const [gameState, setGameState] = useState(null);
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  const userId = localStorage.getItem('userId');
  const username = localStorage.getItem('username');
  const [previousGameState, setPreviousGameState] = useState(null);

  useEffect(() => {
    const interval = setInterval(fetchGameState, 1000);
    return () => clearInterval(interval);
  }, [gameId]);

  useEffect(() => {
    if (gameState && (!previousGameState || gameState.current_round !== previousGameState.current_round)) {
      setAnswer('');
    }
    setPreviousGameState(gameState);

    // Auto-join next game if available
    if (gameState?.status === 'finished' && gameState?.next_game && username) {
      joinNextGame(gameState.next_game);
    }
  }, [gameState]);

  const joinNextGame = async (nextGameId) => {
    try {
      const response = await fetch(`${API_URL}/api/game/${nextGameId}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });

      if (response.status === 201) {
        const userData = await response.json();
        localStorage.setItem('userId', userData.id);
        window.location.href = `/game/${nextGameId}`;
      }
    } catch (err) {
      console.error('Failed to join next game:', err);
    }
  };

  const fetchGameState = async () => {
    try {
      const response = await fetch(`${API_URL}/api/game/${gameId}`);
      if (!response.ok) throw new Error('Failed to fetch game state');
      const state = await response.json();
      setGameState(state);
    } catch (err) {
      setError('Failed to fetch game state');
    }
  };

  const handleSubmitAnswer = async (e) => {
    e.preventDefault();
    if (!answer || !gameState?.question) return;

    try {
      const response = await fetch(`${API_URL}/api/game/${gameId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-ID': userId,
        },
        body: JSON.stringify({
          answer: parseFloat(answer),
          question_index: gameState.current_round,
        }),
      });

      if (response.ok) {
        setError('');
      } else {
        const data = await response.text();
        setError(data);
      }
    } catch (err) {
      setError('Failed to submit answer');
    }
  };

  if (!gameState) {
    return <div className="loading">Loading game state...</div>;
  }

  const currentUser = gameState.users.find(u => u.username === username);
  const hasAnswered = currentUser?.has_answered;

  const renderGameContent = () => {
    switch (gameState.status) {
      case 'waiting':
        return (
          <div className="game-waiting">
            <h2>Waiting for the game to start</h2>
            <div className="players-list">
              <h3>Players ({gameState.users.length})</h3>
              <div className="players-grid">
                {gameState.users.map(user => (
                  <div key={user.username} className="player-item">
                    {user.username}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'in_progress':
        return (
          <div className="game-in-progress">
            <div className="round-info">
              <h2>Round {gameState.current_round} of {gameState.total_rounds}</h2>
            </div>
            
            {gameState.question && (
              <div className="question-container">
                <div className="question-text">
                  {gameState.question.text}
                </div>
                {!hasAnswered ? (
                  <form onSubmit={handleSubmitAnswer} className="answer-form">
                    <div className="input-group">
                      <input
                        type="number"
                        step="any"
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        placeholder="Your answer"
                      />
                      <button type="submit" disabled={!answer}>
                        Submit
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="answer-status">
                    <p>Accepted answer: {answer}</p>
                  </div>
                )}
              </div>
            )}

            <div className="scoreboard">
              <h3>Scoreboard</h3>
              <div className="scores-list">
                {gameState.users.map(user => (
                  <div key={user.username} className={`score-item ${user.username === username ? 'current-user' : ''}`}>
                    <span className="player-name">{user.username}</span>
                    <span className="player-score">{Math.round(user.score)} pts</span>
                    {user.has_answered && <span className="answered-mark">✓</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'finished':
        return (
          <div className="game-finished">
            <h2>Game Over!</h2>
            <div className="final-scores">
              <h3>Final Scores</h3>
              <div className="scores-list">
                {gameState.users.map(user => (
                  <div key={user.username} className={`score-item ${user.username === username ? 'current-user' : ''}`}>
                    <span className="player-name">{user.username}</span>
                    <span className="player-score">{Math.round(user.score)} pts</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return <div>Unknown game state</div>;
    }
  };

  return (
    <div className="game-view">
      {error && <div className="error-message">{error}</div>}
      {renderGameContent()}
    </div>
  );
}

export default GameView; 